import { useFormik } from "formik";
import React, { useState } from "react";
import { FaChevronRight } from "react-icons/fa6";
import { MdOutlineEmail } from "react-icons/md";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { postApiCallerFormData } from "../../libs/apiCaller";

const EditInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profileInfo = location.state?.profileInfo;
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(
    profileInfo?.profile_picture ||
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTqafzhnwwYzuOTjTlaYMeQ7hxQLy_Wq8dnQg&s"
  );
  // Yup Validation Schema
  const ConsumerValidationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    profile_picture: Yup.mixed().nullable(),
  });

  // Handle Profile Picture Change
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
      formik.setFieldValue("profile_picture", file);
    }
  };

  // Formik Hook
  const formik = useFormik({
    initialValues: {
      first_name: profileInfo?.first_name || "",
      last_name: profileInfo?.last_name || "",
      email: profileInfo?.email || "-",
      country_code: profileInfo?.country_code || "+1",
      number: profileInfo?.number || "",
      profile_picture: profileInfo?.profile_picture || null,
    },
    validationSchema: ConsumerValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setLoading(true);

      try {
        // Convert to FormData
        const formData = new FormData();
        formData.append("first_name", values.first_name);
        formData.append("last_name", values.last_name);
        formData.append("email", values.email);
        formData.append("country_code", values.country_code);
        formData.append("number", values.number);
        formData.append("type", "profile");
        formData.append("consumer_id", profileInfo.id);

        // Append profile picture (only if changed)
        if (values?.profile_picture) {
          formData.append("profile_picture", values.profile_picture);
        }

        // API Request (Replace with your actual API)
        const response = await postApiCallerFormData(
          "consumer_update",
          formData
        );

        toast.success("Consumer updated successfully!");
        navigate(`/consumerProfile/${profileInfo.id}`);
      } catch (error) {
        console.error("Error updating profile:", error);
      }

      setSubmitting(false);
      setLoading(false);
    },
  });

  return (
    <>
      <div className="px-4 md:px-10">
        {/* Breadcrumb */}
        <div className="flex mt-6 text-sm gap-2 items-center">
          <Link to={"/consumer"} className="text-[#284184] text-xl">
            All Consumers
          </Link>
          <FaChevronRight className="text-[#284184] text-md" />
          <Link
            to={`/consumerProfile/${profileInfo.id}`}
            className="text-[#284184] text-xl"
          >
            {profileInfo?.first_name} {profileInfo?.last_name}
          </Link>
          <FaChevronRight className="text-[#284184] text-md" />
          <span className="text-[#284184] text-xl font-bold">Edit Info</span>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="border border-1 px-4 py-3 rounded-t-2xl text-xl mt-4 bg-white">
            <div className="flex justify-between items-center">
              <p className="text-[#284184] text-xl font-bold">
                Edit Profile Info
              </p>
              <button
                type="submit"
                className="bg-blue-700 text-white px-4 py-2 rounded-md hover:bg-blue-800"
                disabled={formik.isSubmitting || loading}
              >
                {loading ? "Loading..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="border border-t-0 rounded-b-2xl px-4 py-4 bg-white">
            {/* Profile Image Upload */}
            <div className="flex gap-4 items-center">
              <img
                src={
                  profileImage ||
                  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTqafzhnwwYzuOTjTlaYMeQ7hxQLy_Wq8dnQg&s"
                }
                alt="profile"
                className="w-24 h-24 rounded-xl object-cover border border-gray-300"
              />
              <label className="relative cursor-pointer">
                <p className="text-base text-[#E0A256] underline">
                  Change Photo
                </p>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            </div>

            {/* Input Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-5">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formik.touched.first_name && formik.errors.first_name && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.first_name}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formik.touched.last_name && formik.errors.last_name && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.last_name}
                  </p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative w-full">
                  <input
                    type="email"
                    name="email"
                    value={formik.values.email}
                    readOnly
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  />
                  <MdOutlineEmail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Mobile Number (Read-Only but Country Code is Selectable) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={`${formik.values.country_code}${formik.values.number}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  disabled={true}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditInfo;
